/**
 * Generic hook for computing entity-level changes between old and current snapshots.
 *
 * Works with any entity type — providers, coverages, tasks, etc.
 */

import { useMemo } from 'react';

import type { ChangeType } from '@/services/api/generated/crossMatterChangesService';

import type { FieldChange } from './deepDiff';
import { computeDeepDiffs } from './deepDiff';

export interface EntityChanges {
  changedPaths: Map<string, FieldChange>;
  totalChanges: number;
  isFieldChanged: (path: string) => boolean;
  hasChangesUnder: (prefix: string) => boolean;
  getOldValue: (path: string) => unknown | undefined;
}

const EMPTY_CHANGES: EntityChanges = {
  changedPaths: new Map(),
  totalChanges: 0,
  isFieldChanged: () => false,
  hasChangesUnder: () => false,
  getOldValue: () => undefined,
};

export function useEntityChanges(
  changeType: ChangeType,
  old: unknown | undefined,
  current: unknown | undefined,
): EntityChanges {
  return useMemo(() => {
    if (changeType !== 'UPDATED' || !old || !current) {
      return EMPTY_CHANGES;
    }

    const changedPaths = computeDeepDiffs(old, current);

    const topLevelKeys = new Set(
      Array.from(changedPaths.keys()).map((path) => path.split('.')[0]),
    );

    return {
      changedPaths,
      totalChanges: topLevelKeys.size,
      isFieldChanged: (p: string) => changedPaths.has(p),
      hasChangesUnder: (prefix: string) =>
        Array.from(changedPaths.keys()).some(
          (key) => key === prefix || key.startsWith(`${prefix}.`),
        ),
      getOldValue: (p: string) => changedPaths.get(p)?.oldValue,
    };
  }, [changeType, old, current]);
}
